/**
 * AutomationHelpModal Component
 *
 * How an AI automation decides, what its policy has to contain, and examples.
 */
import { useId, useState } from 'react';
import { Button, cn, Icon, Loader, Modal } from '@insio/ui-toolkit';
import { AutomationKind } from '@insio/shared-types';
import { HelpItem, HelpSection } from '@/components/help-modal';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useAutomationHelp } from './use-automation-help';

/** Automations that list what their AI can load here; the others show it beside the prompt. */
const TOOLS_IN_HELP_KINDS: ReadonlySet<AutomationKind> = new Set([
  AutomationKind.INVOICE_VALIDATION,
  AutomationKind.INVOICE_APPROVAL,
  AutomationKind.REQUISITION_APPROVAL,
  AutomationKind.PURCHASE_ORDER_APPROVAL
]);

export interface AutomationHelpModalProps {
  open: boolean;
  onClose: () => void;
  kind: AutomationKind;
  /** Title of the automation the help belongs to. */
  title: string;
}

export function AutomationHelpModal({ open, onClose, kind, title }: AutomationHelpModalProps) {
  const t = useFeatureTranslation('systemSettings.automation.help');
  const { help, loading, error } = useAutomationHelp(kind);
  const showLoader = useDelayedLoading(loading && !help);
  const toolsId = useId();
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  // The validation task is an instruction, not an approval policy.
  const writingTitle =
    kind === AutomationKind.INVOICE_VALIDATION
      ? t('writingInstructionsTitle')
      : t('writingRulesTitle');

  const renderContent = () => {
    if (error) {
      return (
        <p className='text-sm text-error-text' data-testid='automation-help-error'>
          {t('loadError')}
        </p>
      );
    }
    if (!help) {
      return showLoader ? <Loader.Section /> : null;
    }
    return (
      <>
        {help.decision.length > 0 && (
          <HelpSection title={t('decisionTitle')} testId='automation-help-decision'>
            {help.decision.map((text, index) => (
              <HelpItem key={index} icon='check' text={text} />
            ))}
          </HelpSection>
        )}
        {TOOLS_IN_HELP_KINDS.has(kind) && help.tools.length > 0 && (
          <section className='flex flex-col gap-sm' data-testid='automation-help-tools'>
            <button
              type='button'
              onClick={() => setIsToolsOpen(prev => !prev)}
              aria-expanded={isToolsOpen}
              aria-controls={toolsId}
              className='flex items-center gap-xs self-start text-left text-sm font-semibold text-foreground-base hover:opacity-70'
              data-testid='automation-help-tools-toggle'
            >
              <Icon
                name='chevron-down'
                className={cn(
                  'h-3 w-3 shrink-0 transition-transform',
                  !isToolsOpen && '-rotate-90'
                )}
              />
              {t('toolsTitle', { count: help.tools.length })}
            </button>
            <div
              id={toolsId}
              hidden={!isToolsOpen}
              className='rounded-lg border border-border-base bg-background-muted px-md py-sm'
            >
              <p className='mb-sm text-sm text-foreground-muted'>{t('toolsDescription')}</p>
              <ul className='flex flex-col gap-xs'>
                {help.tools.map(tool => (
                  <HelpItem key={tool.name} icon='check' text={tool.label} />
                ))}
              </ul>
            </div>
          </section>
        )}
        {help.writingRules.length > 0 && (
          <HelpSection title={writingTitle} testId='automation-help-writing-rules'>
            {help.writingRules.map((text, index) => (
              <HelpItem key={index} icon='check' text={text} />
            ))}
          </HelpSection>
        )}
        {help.examples.length > 0 && (
          <HelpSection title={t('examplesTitle')} testId='automation-help-examples'>
            {help.examples.map((example, index) => (
              <li key={index} className='flex flex-col gap-xs'>
                <blockquote className='rounded-md border border-border-base border-l-4 border-l-primary-base bg-background-muted px-md py-sm text-sm italic text-foreground-base'>
                  {example.policy}
                </blockquote>
                <p className='text-xs text-foreground-muted'>
                  {t('demonstrates')} {example.demonstrates}
                </p>
              </li>
            ))}
          </HelpSection>
        )}
      </>
    );
  };

  return (
    <Modal open={open} onClose={onClose} size='md' testId='automation-help'>
      <Modal.Header onClose={onClose} label={t('title')} description={title} />
      <Modal.Content size='full' className='flex flex-col gap-lg overflow-y-auto px-lg pb-lg'>
        {renderContent()}
      </Modal.Content>
      <Modal.Footer>
        <Button variant='primary' onClick={onClose} testId='automation-help-close'>
          {t('close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
